import { TV_HOME_SHELF_NAV_ITEMS } from "./tvHomeShelfConfig";
import { HOME_STREAMING_PROVIDER_SHELVES } from "./homeStreamingProviderShelves";

import { appleHomePath, appleLibraryPath as buildAppleLibraryPath, appleListPath } from "./appleTvHost";

/** Home page shelf order — shared by the home rails and expanded sidebar. */
export const HOME_SHELF_NAV_ITEMS = [
  { key: "movies", label: "Most Popular Movies" },
  { key: "streaming", label: "New to Streaming" },
  { key: "oscar-best-picture-winners", label: "Best Picture Winners" },
  { key: "prestige-nominees", label: "Recent Best Picture Nominees" },
  ...HOME_STREAMING_PROVIDER_SHELVES.map(({ key, label }) => ({ key, label })),
];

export { TV_HOME_SHELF_NAV_ITEMS };

export const HOME_SHELF_KEYS = new Set([
  ...HOME_SHELF_NAV_ITEMS.map((item) => item.key),
  ...TV_HOME_SHELF_NAV_ITEMS.map((item) => item.key),
]);

export const RECENT_LISTS_STORAGE_KEY = "appleRecentListVisits";
export const RECENT_LISTS_UPDATED_EVENT = "apple-recent-lists-updated";
export const MAX_RECENT_LISTS = 10;

export function homeShelfListPath(key) {
  return appleListPath(key);
}

export function libraryListPath(categoryId, optionId = null, subOptionId = null) {
  const params = new URLSearchParams();
  if (categoryId) params.set("cat", categoryId);
  if (optionId) params.set("opt", optionId);
  if (subOptionId) params.set("sub", subOptionId);
  const text = params.toString();
  return buildAppleLibraryPath(text);
}

export function libraryListKey(categoryId, optionId = null, subOptionId = null) {
  return `lib:${String(categoryId || "")}:${String(optionId || "")}:${String(subOptionId || "")}`;
}

function normalizeRecentListEntry(entry) {
  const key = String(entry?.key || "").trim();
  const label = String(entry?.label || "").trim();
  if (!key || !label) return null;
  const to = String(entry?.to || "").trim() || (
    key.startsWith("lib:")
      ? libraryListPath(
        key.split(":")[1] || "",
        key.split(":")[2] || null,
        key.split(":")[3] || null,
      )
      : homeShelfListPath(key)
  );
  return {
    key,
    label,
    to,
    visitedAt: Number(entry?.visitedAt || 0),
  };
}

function recentListKeys(entries) {
  return entries.map((entry) => entry.key).join("\0");
}

function writeRecentListVisits(entries, { notify = true } = {}) {
  if (typeof window === "undefined") return undefined;
  try {
    window.localStorage.setItem(RECENT_LISTS_STORAGE_KEY, JSON.stringify(entries));
    if (notify) {
      window.dispatchEvent(new CustomEvent(RECENT_LISTS_UPDATED_EVENT));
    }
  } catch {
    // Ignore quota / private-mode storage failures.
  }
  return undefined;
}

let recentListReorderTimer = null;

function pushRecentListVisit(entry, { deferReorder = true } = {}) {
  const normalized = normalizeRecentListEntry(entry);
  if (!normalized || normalized.key === "related") return;
  if (typeof window === "undefined") return undefined;

  const apply = () => {
    try {
      const previous = readRecentListVisits({ sorted: false });
      const existingIndex = previous.findIndex((item) => item.key === normalized.key);
      if (existingIndex === 0) {
        const current = previous[0];
        if (current.label === normalized.label && current.to === normalized.to) {
          return;
        }
      }

      const withoutCurrent = previous.filter((item) => item.key !== normalized.key);
      const next = [
        { ...normalized, visitedAt: Date.now() },
        ...withoutCurrent,
      ].slice(0, MAX_RECENT_LISTS);

      const orderChanged = recentListKeys(previous) !== recentListKeys(next);
      writeRecentListVisits(next, { notify: orderChanged });
    } catch {
      // Ignore quota / private-mode storage failures.
    }
  };

  if (!deferReorder) {
    apply();
    return undefined;
  }

  if (recentListReorderTimer) {
    window.clearTimeout(recentListReorderTimer);
  }
  recentListReorderTimer = window.setTimeout(() => {
    recentListReorderTimer = null;
    apply();
  }, 180);
  return undefined;
}

export function recordRecentListVisit(key, label, to = null) {
  const listKey = String(key || "").trim();
  const listLabel = String(label || "").trim();
  if (!listKey || listKey === "related" || !listLabel) return;
  pushRecentListVisit({
    key: listKey,
    label: listLabel,
    to: to || homeShelfListPath(listKey),
  });
}

export function recordRecentLibraryVisit(categoryId, optionId, subOptionId, label) {
  const listLabel = String(label || "").trim();
  if (!categoryId || !optionId || !listLabel) return;
  pushRecentListVisit({
    key: libraryListKey(categoryId, optionId, subOptionId),
    label: listLabel,
    to: libraryListPath(categoryId, optionId, subOptionId),
  });
}

export function readRecentListVisits({ sorted = true } = {}) {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENT_LISTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const entries = parsed
      .map(normalizeRecentListEntry)
      .filter(Boolean);
    if (!sorted) return entries;
    return entries.sort((a, b) => Number(b.visitedAt || 0) - Number(a.visitedAt || 0));
  } catch {
    return [];
  }
}

/** Recent list pages for the sidebar — excludes shelves already pinned under Home. */
export function getSidebarRecentListVisits() {
  return readRecentListVisits().filter((entry) => !HOME_SHELF_KEYS.has(entry.key));
}

export function recentListEntryPath(entry) {
  if (!entry) return appleHomePath();
  return String(entry.to || "").trim() || homeShelfListPath(entry.key);
}

export function areRecentListVisitsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((entry, index) => {
    const other = b[index];
    return entry?.key === other?.key
      && entry?.label === other?.label
      && entry?.to === other?.to;
  });
}
